import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import { Delete, Pen } from "lucide-react";
import Swal from "sweetalert2";
import {
  addRole,
  deleteRole,
  fetchRoles,
  updateRole,
} from "../../../slices/company/rolesSlice";
import { customTableStyles } from "../../../utils/tableStylings";

const CompanyRoles = () => {
  const dispatch = useDispatch();
  const { roles } = useSelector((state) => state.companyRoles) || {};
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [slug, setSlug] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedModules, setSelectedModules] = useState({});
  const [showForm, setShowForm] = useState(false);


  // Define module permissions structure
  const modulePermissions = [
    {
      module: "Dashboard",
      permissions: [],
    },
    {
      module: "POS",
      permissions: [],
    },
    {
      module: "Roles",
      permissions: ["roles", "task permissions"],
    },
    {
      module: "Branch",
      permissions: ["List", "Modify"],
    },
    {
      module: "Users",
      permissions: ["List", "Modify"],
    },
    {
      module: "Master setup",
      permissions: ["supplier", "Customer", "taxes", "units", "brands", "product attribute"],
    },
    {
      module: "Import",
      permissions: ["products", "images"],
    },
    {
      module: "Purchase",
      permissions: ["Category", "Master", "Stock"],
    },
    {
      module: "Final products",
      permissions: ["Category", "Products"],
    },
    {
      module: "Purchase system",
      permissions: ["Request", "Approved", "Allocate", "Entry"],
    },
    {
      module: "Sales System",
      permissions: ["Sales Executive", "Request", "Cost Estimation", "Orders", "Delivered"],
    },
    {
      module: "Production",
      permissions: ["Orders"],
    },
    {
      module: "Accounts",
      permissions: ["Sales Bill", "Sales Bill Export", "Bills", "Credit Note", "Assets", "Expenses"],
    },
  ];

  // Toggle module permission
  const toggleModulePermission = (module, perm) => {
    setSelectedModules((prev) => {
      const current = prev[module] || [];
      if (current.includes(perm)) {
        return { ...prev, [module]: current.filter((p) => p !== perm) };
      } else {
        return { ...prev, [module]: [...current, perm] };
      }
    });
  };

  const permissions = [
    "Sales",
    "Orders",
    "Stock",
    "Purchases",
    "Production",
    "Return",
    "Accounts",
    "Data and Clerical",
    "Human Resource",
    "Master Setup",
  ];

  const columns = [
    { name: "#", selector: (row, index) => index + 1, width: "50px" },
    { name: "Role Name", selector: (row) => row.name, sortable: true },
    { name: "Slug", selector: (row) => row.slug, sortable: true },
    {
      name: "Status",
      cell: (row) => (
        <Switch
          checked={row.status === 1}
          onChange={(checked) => handleStatusToggle(row.id, checked)}
          offColor="#d6d6d6"
          onColor="#4caf50"
          uncheckedIcon={false}
          checkedIcon={false}
        />
      ),
      sortable: true,
    },
    {
      name: "Actions",
      button: true,
      cell: (row) => (
        <div>
          <Link className="text-success me-2" onClick={() => handleEdit(row)}>
            <Pen />
          </Link>
          <Link className="text-danger btn-sm" onClick={() => handleDelete(row.id)}>
            <Delete />
          </Link>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const company = JSON.parse(localStorage.getItem("company"));

    const roleData = {
      company_id: company?.company?.id,
      name: roleName,
      slug,
      status: 1,
      permissions: selectedPermissions, // Task permissions
      module_permissions: selectedModules, // Module permissions
    };

    if (selectedRole?.id) {
      dispatch(updateRole({ id: selectedRole.id, roleData }));
    } else {
      dispatch(addRole(roleData));
    }

    resetForm();
  };

  const resetForm = () => {
    setSelectedRole(null);
    setRoleName("");
    setSlug("");
    setSelectedPermissions([]);
    setSelectedModules({});
  };

  const generateSlug = (name) =>
    name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "")
      .replace(/--+/g, "-")
      .replace(/^-+|-+$/g, "");

  useEffect(() => {
    if (roleName) setSlug(generateSlug(roleName));
  }, [roleName]);

  const handleStatusToggle = (id, checked) => {
    dispatch(updateRole({ id, roleData: { status: checked } }));
  };

  const handleEdit = (role) => {
    // setSelectedRole(role);
    // setRoleName(role.name);
    // setSlug(role.slug);
    // setSelectedPermissions(JSON.parse(role.permissions) || []);
    // setSelectedModules(role.module_permissions ? JSON.parse(role.module_permissions) : {});
    setSelectedRole(role);
    setRoleName(role.name);
    setSlug(role.slug);
    setSelectedPermissions(JSON.parse(role.permissions) || []);
    setSelectedModules(
      role.module_permissions ? JSON.parse(role.module_permissions) : {}
    );
    setShowForm(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "<strong>Are you sure?</strong>",
      html: "<p>This action will <b>permanently delete</b> this role.</p>",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteRole(id));
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The Role has been deleted.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    });
  };

  const togglePermission = (permission) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((perm) => perm !== permission)
        : [...prev, permission]
    );
  };

  return (
    <div className="row">
      <div className={`${showForm ? "col-8" : "col-12"}`}>
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center gap-2">
            <h3 className="mb-0 text-primary">Roles</h3>

            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                placeholder="Search Roles"
                className="form-control"
                style={{ width: "220px" }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                Add Role
              </button>
            </div>
          </div>

          <div className="card-body">

            <div className="table-responsive">
              <DataTable
                className="table-hover custom-table"
                columns={columns}
                data={roles}
                pagination
                striped
                progressPending={loading}
                customStyles={customTableStyles}
              />
            </div>
          </div>
        </div>
      </div>

      {showForm &&(<div className={`col-4 slide-form ${showForm ? "open" : ""}`}>
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5 className="card-title">
              {selectedRole ? "Edit Role" : "Add New Role"}
            </h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => setShowForm(false)}
            >
              ✕
            </button>
          </div>
          <div className="card-body">
            <form onSubmit={handleFormSubmit}>
              <div className="mb-3">
                <label htmlFor="roleName" className="form-label">
                  Role Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="roleName"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="slug" className="form-label">
                  Slug
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required
                />
              </div>

              {/* Module Permissions Section */}
              <div className="mb-3">
                <label className="form-label">Module Permissions</label>
                <div className="">
                  {modulePermissions.map((mod) => (
                    <div key={mod.module} className="mb-2">
                      <strong>{mod.module}</strong>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {mod.permissions.length === 0 ? (
                          <div className={`p-2 border rounded ${selectedModules[mod.module]?.length ? "bg-success text-white" : "border-success"}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleModulePermission(mod.module, mod.module)}>
                            {mod.module}
                          </div>
                        ) : (
                          mod.permissions.map((perm) => (
                            <div key={perm}
                              className={`p-2 border rounded ${selectedModules[mod.module]?.includes(perm) ? "bg-success text-white" : "border-success"}`}
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleModulePermission(mod.module, perm)}>
                              {perm}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Task Permissions</label>
                <div className="d-flex flex-wrap gap-2">
                  {permissions.map((perm) => (
                    <div
                      key={perm}
                      className={`p-2 border rounded ${selectedPermissions.includes(perm)
                        ? "bg-success text-white"
                        : "border-success"
                        }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => togglePermission(perm)}
                    >
                      {perm}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <button type="submit" className="btn btn-primary">
                  {selectedRole ? "Update" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>)}
    </div>
  );
};

export default CompanyRoles;
